import {ServiceBase} from "../ServiceBase";
import {IInGameChallengeService} from "./in-game-challenge.service.interface";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {IFirebaseTopicRegistration} from "../firebase/topics/firebase-topic-view-model.interface";
import {IInGameCampaign} from "../firebase/topics/in-game-challenges/add-to-campaign/add-to-campaign-topic.model.interface";
import {Nullable} from "../../../common/types/nullable-types";
import {InGameChallengeCampaignModel} from "./in-game-challenge-campaign.model";
import {makeObservable, observable, runInAction} from "mobx";
import {LocalStorageKeys} from "../storage/local-storage-keys";
import {OrbitUserFlowAnalyticsCategory} from "../analytics/analytics.service.interface";
import {NEW_TICKET_NOTIFICATION} from "../magic-8-ball/new-ticket-notification-strategy.enum";

export class InGameChallengeService extends ServiceBase implements IInGameChallengeService {
    constructor(services: IServiceFactoryExtended) {
        super(services);

        makeObservable(this);
        this.reaction(() => this.services.user.isAuthorized,
            (isAuth) => {
                if (isAuth) {
                    this._registerToCampaignsUpdates();
                } else {
                    this.dispose();
                }
            }, {
            fireImmediately: true
        });

        this.reaction(() => Boolean(this._activeCampaign?.isActive), (isActive) => {
            if (!isActive) {
                this._activateCampaign();
            }
        })
    }

    @observable.ref
    private _activeCampaign: InGameChallengeCampaignModel | null = null;

    get currentProgressPercentage(): number {
        const currentCampaignPercentage = this._activeCampaign?.progressPercentage ?? 0;
        return currentCampaignPercentage >= 100 ? 100 : currentCampaignPercentage % 100;
    }

    get allActiveCampaignRulesCompleted() {
        return this._activeCampaign?.allCampaignRulesCompleted ?? false;
    }

    get hasActiveCampaign(): boolean {
        return Boolean(this._activeCampaign);
    }

    get campaignEndDate(): Date | null {
        return this._activeCampaign?.endDate ?? null;
    }

    private _allCampaigns: Record<number, InGameChallengeCampaignModel> = {};

    private _firebaseCampaignsRegistration: Nullable<IFirebaseTopicRegistration> = null;

    private _registerToCampaignsUpdates() {
        this._firebaseCampaignsRegistration = this.services.firebase.topics.inGameChallengesAddToCampaign.registerListener(this._handleCampaignsUpdate.bind(this));
    }

    private _handleCampaignsUpdate(campaigns: IInGameCampaign[]) {
        this.services.logger.debug(`InGame Challenge: received campaigns update: ${JSON.stringify(campaigns, null, 2)}`);
        for (let campaign of campaigns) {
            if (this._allCampaigns[campaign.CampaignId]) {
                this._allCampaigns[campaign.CampaignId].updateData(campaign);
            } else {
                this._allCampaigns[campaign.CampaignId] = new InGameChallengeCampaignModel(campaign, this.services);
            }
        }

        this._activateCampaign();
    }

    private _activateCampaign(): void {
        const firstActiveCampaign = Object.values(this._allCampaigns)
            .sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
            .findFirst((c => c.isActive));

        if (firstActiveCampaign) {
            this.services.logger.debug(`InGame Challenge: activating campaign ${firstActiveCampaign.id}`);
            if (this._activeCampaign?.id !== firstActiveCampaign.id) {
                this._activeCampaign?.stopProgressSubscription();
                runInAction(() => {
                    this._activeCampaign = firstActiveCampaign;
                    this._activeCampaign.startProgressSubscription();
                    this._setNewTicketNotificationStrategy();
                });
            }
        } else {
            this._activeCampaign?.stopProgressSubscription();
            runInAction(() => {
                this._activeCampaign = null;
            });
        }
    }

    _setNewTicketNotificationStrategy() {
        const lastCampaignExpirationData = this.services.localStorage.getItem(LocalStorageKeys.igcLastShownCampaignExpiration);
        if (lastCampaignExpirationData === this._activeCampaign?.campaignExpirationStorageData) {
            this.services.magic8ball.setNewTicketNotificationStrategy(NEW_TICKET_NOTIFICATION.BALL_PUMP);
        } else {
            this.services.magic8ball.setNewTicketNotificationStrategy(NEW_TICKET_NOTIFICATION.EXPIRATION_TOOLTIP);
        }
    }

    updateCampaignExpirationTooltipData() {
        const data = this._activeCampaign?.campaignExpirationStorageData;
        this.services.localStorage.setItem(LocalStorageKeys.igcLastShownCampaignExpiration, data);
    }

    sendAnalytics(action: string, label: string, value?: string) {
        this.services.analytics.sendOrbitUserFlowEvent(
            OrbitUserFlowAnalyticsCategory.InGameChallenge, // Category
            action,
            label,
            value
        );
    }

    dispose(): void {
        this._firebaseCampaignsRegistration?.unregister();
        this._firebaseCampaignsRegistration = null;

        Object.values(this._allCampaigns).forEach(campaign => {
            campaign.dispose();
        })

        runInAction(() => {
            this._allCampaigns = {};
            this._activeCampaign = null;
        })
    }
}